import { Link } from 'react-router';
import { Button } from './Button';
import { useDelayed } from './useDelayed';
import styles from './States.module.css';
import buttonStyles from './Button.module.css';

/**
 * Why line: 14 px --ink2, one line. The only place middle dots are allowed
 * (fail list 4). "From Soul Jazz Vault · soul-jazz · matched: organ, horns"
 */
export function WhyLine({ from, parts }: { from: string; parts: string[] }) {
  return (
    <p className={styles.why}>
      From <i>{from}</i>
      {parts.map((p) => ` · ${p}`).join('')}
    </p>
  );
}

type Action = { label: string; to: string; onClick?: never } | { label: string; onClick: () => void; to?: never };

/** One line saying what goes here, plus one action. No illustration, no mood copy. */
export function EmptyState({ message, action }: { message: string; action?: Action }) {
  return (
    <div className={styles.state}>
      <p>{message}</p>
      {action?.to && (
        <Link to={action.to} className={`${buttonStyles.button} ${buttonStyles.secondary}`}>
          {action.label}
        </Link>
      )}
      {action?.onClick && <Button onClick={action.onClick}>{action.label}</Button>}
    </div>
  );
}

/** What happened, what to do, and a retry action. */
export function ErrorState({ problem, fix, onRetry, retrying }: {
  problem: string; fix: string; onRetry: () => void; retrying?: boolean;
}) {
  return (
    <div className={styles.state} role="alert">
      <p className={styles.problem}>{problem}</p>
      <p className={styles.fix}>{fix}</p>
      <Button onClick={onRetry} loading={retrying} loadingLabel="Trying again">Try again</Button>
    </div>
  );
}

/**
 * Feedback budget (DESIGN_SYSTEM §5): nothing for 300 ms, then inline text;
 * after 2 s a determinate bar with Cancel when progress is known.
 */
export function Progress({ active, label, value, onCancel }: {
  active: boolean; label: string; value?: number; onCancel?: () => void;
}) {
  const inline = useDelayed(active, 300);
  const long = useDelayed(active, 2000);
  if (!inline) return null;
  const pct = value === undefined ? undefined : Math.round(Math.max(0, Math.min(1, value)) * 100);
  return (
    <div className={styles.progress} role="status">
      <div className={styles.progressRow}>
        <span className="t-meta">{pct !== undefined && long ? `${label}, ${pct}%` : label}</span>
        {long && onCancel && <Button variant="tertiary" onClick={onCancel}>Cancel</Button>}
      </div>
      {long && pct !== undefined && (
        <div className={styles.bar} role="progressbar" aria-label={label} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <span className={styles.barFill} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}
